<template>
    <div class="w-full bg-white p-6 rounded-xl shadow-sm">
        
        <!-- ONBOARDING RIBBON (STEP 4 - FINAL) -->
        <div v-if="showOnboardingRibbon" 
             class="w-full mb-6 rounded-lg border px-6 py-3 flex items-center justify-between animate-fade-in-up"
             style="background-color: #e8f5e9; border-color: #c8e6c9; color: #1b5e20;">
            <div class="flex items-center gap-3">
                <span style="font-size:20px">🎉</span>
                <span class="text-sm md:text-base">
                    <strong>Final Step:</strong> You are on Step 4 of 4. Review your talent insights below.
                </span>
            </div>
            
            <div @click="finishOnboarding" 
                 class="cursor-pointer font-bold px-4 py-2 rounded-lg transition-all shadow-sm hover:shadow-md flex items-center gap-2"
                 style="background-color: #fff; color: #2e7d32; border: 1px solid #a5d6a7;">
                Finish Setup 
                <i class="vs-icon notranslate icon-scale feather icon-check-circle"></i>
            </div>
        </div>

        <!-- TABS HEADER -->
        <div class="pb-4 flex items-center overflow-x-auto">
            <div v-for="option in communityVisiblityOptions" :key="option.id"
                class="flex items-center cursor-pointer relation-btn p-3 mx-2 shrink-0" :class="{
                    'relation-btn-active': selectedVisibilityOption === option.id,
                }" @click="selectedVisibilityOption = option.id">
                <img v-if="selectedVisibilityOption === option.id" class="w-6"
                    :src="require(`@/assets/images/icons/${option.activeIcon}.svg`)" :alt="option.id" />
                <img v-else class="w-6" :src="require(`@/assets/images/icons/${option.inactiveIcon}.svg`)"
                    :alt="option.id" />
                <h6 class="ml-2 text-lg font-semibold capitalize whitespace-nowrap" :class="selectedVisibilityOption === option.id
                    ? 'text-white'
                    : 'text-black'
                    ">
                    {{ option.titleKey ? $t(option.titleKey) : option.id }}
                </h6>
            </div>
        </div>

        <!-- SEARCH INPUTS -->
        <div v-if="selectedVisibilityOption === 'network'" class="con-input-search vs-table--search mb-4">
            <input type="text" class="input-search vs-table--search-input" :placeholder="$t('orgDashboard.talentPool.networkSearchPlaceholder')" @input="debounceInput" />
            <i class="vs-icon notranslate icon-scale material-icons null">search</i>
        </div>
        <div v-if="selectedVisibilityOption === 'hierarchy'" class="con-input-search vs-table--search mb-4">
            <input type="text" class="input-search vs-table--search-input" :placeholder="$t('orgDashboard.talentPool.hierarchySearchPlaceholder')" id="hierarchy-search-box" />
            <i class="vs-icon notranslate icon-scale material-icons null">search</i>
        </div>

        <!-- CHARTS / COMPONENTS AREA -->
        <!-- FIX: Show loader while data is being prepared to prevent child component crashes -->
        <div v-if="!isDataReady" class="w-full h-64 flex flex-col justify-center items-center text-gray-500">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
            <span>Loading Data...</span>
        </div>

        <!-- Only render components when data is strictly ready -->
        <div v-else>
            <kvdr-plot 
                v-if="selectedVisibilityOption === $t('orgDashboard.reports.nineCellMatrix')" 
                :assessmentData="kvdrEmployeeData.assessmentData"
                :employeeNames="kvdrEmployeeData.employeeNames" />

            <kvdr-matrix 
                v-if="selectedVisibilityOption === $t('orgDashboard.reports.xyPlot')"
                :assessmentData="kvdrEmployeeData.assessmentData" 
                :employeeNames="kvdrEmployeeData.employeeNames" />

            <talent-scorecard 
                v-if="selectedVisibilityOption === $t('orgDashboard.reports.talentScoreCard')" />

            <keep-alive>
                <network-graph v-if="selectedVisibilityOption === 'network'" />
                <OrganiGram v-if="selectedVisibilityOption === 'hierarchy'" :import-data="employeesData" />
            </keep-alive>
        </div>
    </div>
</template>

<script>
import marketplaceList from "@/assets/data/marketplace-list.js";
import TalentScorecard from "./TalentScoreCard.vue";
import KvdrPlot from "./NineCellMatrix.vue";
import KvdrMatrix from "./XYPlot.vue";
import NetworkGraph from "../users/components/NetworkGraph.vue";
import OrganiGram from "../users/components/OrganiGram/OrganiGram.vue";
import debounce from "debounce";

export default {
    components: {
        KvdrPlot,
        KvdrMatrix,
        TalentScorecard,
        NetworkGraph,
        OrganiGram,
    },
    data() {
        return {
            isDataReady: false, // Controls visibility to prevent crashes
            showOnboardingRibbon: false,
            marketplaceList,
            communityVisiblityOptions: [
                {
                    id: this.$t('orgDashboard.reports.talentScoreCard'),
                    activeIcon: "activeicon-kvdr",
                    inactiveIcon: "inactiveicon-kvdr",
                },
                {
                    id: this.$t('orgDashboard.reports.xyPlot'),
                    activeIcon: "activeicon-network",
                    inactiveIcon: "inactiveicon-network",
                },
                {
                    id: this.$t('orgDashboard.reports.nineCellMatrix'),
                    activeIcon: "activeicon-hierarchy",
                    inactiveIcon: "inactiveicon-hierarchy",
                },
                {
                    id: "hierarchy",
                    activeIcon: "activeicon-hierarchy",
                    inactiveIcon: "inactiveicon-hierarchy",
                    titleKey: "orgDashboard.talentPool.viewOptions.hierarchy"
                },
                {
                    id: "network",
                    activeIcon: "activeicon-network",
                    inactiveIcon: "inactiveicon-network",
                    titleKey: "orgDashboard.talentPool.viewOptions.network"
                },
            ],
            selectedVisibilityOption: this.$t('orgDashboard.reports.talentScoreCard'),
            // FIX: Initialize with correct structure so child props are never undefined
            kvdrEmployeeData: {
                assessmentData: [],
                employeeNames: []
            },
        };
    },
    watch: {
        async orgData(val) {
            if (val) {
                await this.initDashboardData();
            }
        },
    },
    computed: {
        userId() {
            return this.$store.state.auth.user._id;
        },
        orgData() {
            return this.$store.state.organization.orgData;
        },
        kvdrData() {
            return this.$store.state.kvdr.selectedKVDR;
        },
        employeesData() {
            return this.$store.state.organization.employees;
        },
    },
    methods: {
        finishOnboarding() {
            if (this.orgData && this.orgData.slug) {
                localStorage.setItem(`onboardingStep_${this.orgData.slug}`, '5');
                this.$router.push(`/organization/${this.orgData.slug}/dashboard`);
            }
        },
        debounceInput: debounce(function (e) {
            this.$store.commit("community/SET_SEARCH_TEXT", e.target.value);
        }, 500),

        async initDashboardData() {
            this.isDataReady = false;
            try {
                // 1. Fetch Employees FIRST
                await this.fetchOrgEmployees();
                // 2. Then Fetch KVDR (Reports)
                await this.setKVDRState();
            } catch (e) {
                console.error("Dashboard Init Error:", e);
            } finally {
                // 3. Only show components when data is ready
                this.isDataReady = true; 
            }
        },

        async setKVDRState() {
            try {
                var res = await this.$store.dispatch("kvdr/findKVDR", this.orgData._id);
                
                if (!res.data || !res.data.length) {
                    this.$vs.notify({
                        title: this.$t('orgDashboard.reports.emptyReports'),
                        text: this.$t('orgDashboard.reports.createKVDRFirst'),
                        iconPack: "feather",
                        icon: "icon-alert-circle",
                        color: "warning",
                        duration: 10000,
                    });
                    return;
                }

                // FIX: Filter out assessments for users who don't exist in the employee list
                // This prevents "reading 'fullName' of undefined" in child components
                const validEmployeeIds = new Set(this.employeesData.map(e => e._id));
                
                // Assume the report has a user or userId field. We filter based on validity.
                // If we don't know the exact field, we at least pass the raw data but assigned properly.
                const rawData = res.data.filter(item => item !== null && item !== undefined);

                // FIX: Assign the data to the local variable so props work
                this.kvdrEmployeeData = {
                    assessmentData: rawData,
                    employeeNames: this.employeesData || []
                };

            } catch (error) {
                console.log("KVDR Error:", error);
            }
        },

        async fetchOrgEmployees() {
            try {
                var res = await this.$store.dispatch(
                    "organization/getAllOrgEmployees",
                    this.orgData._id
                );
                if (res && res.data && res.data.length > 0) {
                    const updatedObj = res.data.map((item) => {
                        // FIX: Safety check for userData to prevent crash
                        const firstName = item.userData?.firstName || "Unknown";
                        const lastName = item.userData?.lastName || "";
                        
                        return {
                            ...item,
                            fullName: `${firstName} ${lastName}`.trim(),
                        };
                    });
                    this.$store.commit("organization/SAVE_EMPLOYEES", updatedObj);
                }
            } catch (error) {
                console.log("Employee Fetch Error:", error);
            }
        },
    },

    async created() {
        if (this.orgData && this.orgData._id) {
            await this.initDashboardData();
        }
    },
    mounted() {
        if (this.$route.query.onboarding_active === 'true') {
            this.showOnboardingRibbon = true;
        }
    }
};
</script>

<style lang="scss" scoped>
.relation-btn {
    border-radius: 2em;
    border: 1px solid rgba(0, 0, 0, 0.1);
    width: fit-content;

    &-active {
        background: rgba(var(--vs-primary, "#F2C244"), 1);
        border-width: 0;
    }
}
</style>